import { Link, useParams } from "react-router-dom";
import { ArrowRight, Clock, MapPin } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { StopsMap } from "../components/site/StopsMap";
import { FadeUp } from "../components/site/Motion";
import { formatARS, INCLUDES } from "../lib/site";
import { useLivePrices, priceOf } from "../lib/livePrices";
import { duration, placeLabel, routePage, routePagesList, routePath } from "../lib/routePages";
import { useSeo } from "../lib/seo";

const plusDay = (s, b) => Math.floor(b.abs / 1440) - Math.floor(s.abs / 1440);

const Service = ({ s, n }) => {
  const days = plusDay(s.stops[0], s.arrival);
  return (
    <FadeUp>
      <article data-testid={`route-service-${n}`} className="rounded-3xl bg-white p-6 shadow-sm md:p-8">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--forest)]">Recorrido {s.routeName}</p>
        <p data-testid={`route-days-${n}`} className="mt-2 font-semibold">Sale: {s.days}</p>
        <div className="mt-5 grid gap-6 md:grid-cols-3">
          <div><p className="text-sm text-[var(--muted-ink)]">Subís en</p>{s.boardings.map((b) => <p key={b.name} className="font-semibold">{b.name} · {b.time} hs</p>)}</div>
          <div><p className="text-sm text-[var(--muted-ink)]">Llegás a</p><p className="font-semibold">{s.arrival.name} · {s.arrival.time} hs{days > 0 && ` (+${days} día${days > 1 ? "s" : ""})`}</p></div>
          <div><p className="text-sm text-[var(--muted-ink)]">Duración aproximada</p><p data-testid={`route-duration-${n}`} className="flex items-center gap-2 font-semibold"><Clock className="h-4 w-4 text-[var(--forest)]" /> {duration(s.minutes)}</p></div>
        </div>
        <details className="mt-6 group">
          <summary className="cursor-pointer text-sm font-bold text-[var(--forest)]">Ver todas las paradas ({s.stops.length})</summary>
          <ol className="mt-4 space-y-2 border-l-2 border-dashed border-[var(--forest)]/40 pl-5 text-sm">
            {s.stops.map((x) => <li key={x.name} className="flex justify-between gap-4"><span>{x.name}</span><span className="font-mono text-[var(--forest)]">{x.time}</span></li>)}
          </ol>
        </details>
      </article>
    </FadeUp>
  );
};

const Related = ({ page }) => {
  const others = routePagesList().filter((p) => p.slug !== page.slug && (p.origin === page.origin || p.destination === page.destination)).slice(0, 8);
  if (!others.length) return null;
  return (
    <div className="mt-16">
      <h2 className="font-display text-base md:text-lg">Otros pasajes desde {page.origin} y hacia {page.destination}</h2>
      <ul className="mt-4 flex flex-wrap gap-2">
        {others.map((p) => <li key={p.slug}><Link to={routePath(p)} data-testid={`related-${p.slug}`} className="inline-block rounded-full bg-white px-4 py-2 text-sm font-semibold transition-colors hover:bg-[var(--lime)]">{p.origin} → {p.destination}</Link></li>)}
      </ul>
    </div>
  );
};

export default function RutaPasaje() {
  const { slug } = useParams();
  const prices = useLivePrices();
  const base = routePage(slug);
  const page = base && { ...base, semicama: priceOf(prices, base.origin, base.destination, 0) ?? base.semicama, cama: priceOf(prices, base.origin, base.destination, 1) ?? base.cama };
  const best = page ? Math.min(...page.services.map((s) => s.minutes)) : 0;
  useSeo(page
    ? { title: `Pasajes de micro ${placeLabel(page.origin)} a ${placeLabel(page.destination)} | Santiagueño`,
        description: `Pasajes en micro de ${placeLabel(page.origin)} a ${placeLabel(page.destination)} con Santiagueño desde ${formatARS(page.semicama)} en semicama. Salidas ${page.services.map((s) => s.days.toLowerCase()).join(" / ")}, paradas y unas ${duration(best)} de viaje.`,
        path: `/pasajes/${page.slug}` }
    : { title: "Recorrido no encontrado | Santiagueño", description: "Este recorrido no existe.", path: `/pasajes/${slug}`, noindex: true });

  if (!page) {
    return (
      <section data-testid="route-not-found" className="mx-auto max-w-xl px-5 py-32 text-center">
        <h1 className="font-display text-4xl">Recorrido no encontrado</h1>
        <Link to="/destinos" className="mt-6 inline-block font-bold text-[var(--forest)] underline">Ver todos los recorridos</Link>
      </section>
    );
  }
  const search = `/pasajes?o=${encodeURIComponent(page.origin)}&d=${encodeURIComponent(page.destination)}`;
  return (
    <>
      <PageHero testid="route-hero" eyebrow="Pasajes de micro" lines={["Pasajes de", `${page.origin} a ${page.destination}`]}
        intro={`Viajá de ${page.origin} a ${page.destination} con Santiagueño, en unas ${duration(best)} según el horario estimado.`}>
        <div data-testid="route-prices" className="mt-8 flex flex-wrap gap-3">
          <p className="rounded-2xl bg-white px-5 py-3"><span className="block text-xs text-[var(--muted-ink)]">Semicama · piso de arriba</span><b data-testid="route-price-semi" className="text-2xl text-[var(--forest)]">{formatARS(page.semicama)}</b></p>
          {page.cama && <p className="rounded-2xl bg-white px-5 py-3"><span className="block text-xs text-[var(--muted-ink)]">Coche cama · piso de abajo</span><b data-testid="route-price-cama" className="text-2xl text-[var(--forest)]">{formatARS(page.cama)}</b></p>}
        </div>
        <p className="mt-2 text-xs text-[var(--muted-ink)]">Precio por pasaje, IVA incluido.</p>
        <Link to={search} data-testid="route-search-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[var(--forest)] px-7 py-4 font-bold text-white transition-transform hover:-translate-y-0.5">Ver salidas y precios <ArrowRight className="h-4 w-4" /></Link>
      </PageHero>
      <section className="bg-[var(--paper)] pb-24">
        <div className="mx-auto max-w-[1400px] space-y-6 px-5 md:px-10">
          <h2 className="font-display text-base md:text-lg">Horarios y paradas de {page.origin} a {page.destination}</h2>
          {page.services.map((s, n) => <Service key={s.routeId} s={s} n={n} />)}
          <p className="text-xs text-[var(--muted-ink)]">* Horarios estimados: en las paradas intermedias es el horario de salida y en el destino, el de llegada. Pueden cambiar sin previo aviso.</p>
          <StopsMap services={page.services} origin={page.origin} destination={page.destination} />
          <div className="mt-12 rounded-3xl bg-[var(--ink)] p-6 text-white md:p-8">
            <h2 className="font-display text-base md:text-lg">Qué incluye tu viaje</h2>
            <ul className="mt-4 grid gap-2 text-sm text-white/85 sm:grid-cols-2">{INCLUDES.map((i) => <li key={i}>· {i}</li>)}</ul>
          </div>
          <p className="flex items-start gap-2 text-sm"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[var(--forest)]" /> ¿Necesitás mandar algo a {page.destination}? <Link to="/puntoenvio" className="font-bold text-[var(--forest)] underline">Enviá tu encomienda con PuntoEnvío</Link>.</p>
          <Related page={page} />
        </div>
      </section>
    </>
  );
}
